import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { pb, getCurrentUser, hasAdminPermission } from "../services/pocketbase";
import FAQEditor from "../components/FAQEditor";
// eslint-disable-next-line
import ReactMarkdown from "react-markdown";

// Discord-style markdown formatting function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const user = getCurrentUser();
  const isAdmin = hasAdminPermission(user);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        const records = await pb.collection("faqs").getList(1, 100, {
          sort: "position",
        });
        setFaqs(records.items);
      } catch (err) {
        console.error("Error fetching FAQs:", err);
        setError("Failed to load FAQs");
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const handleAddFaq = () => {
    setSelectedFaq(null);
    setEditorOpen(true);
  };

  const handleEditFaq = (faq) => {
    setSelectedFaq(faq);
    setEditorOpen(true);
  };

  const sendDeleteWebhookNotification = async (faqData) => {
    try {
      const timestamp = new Date().toISOString();
      const webhookData = {
        content: "FAQ Deleted",
        embeds: [
          {
            title: "FAQ Deleted",
            color: 0xff0000,
            fields: [
              {
                name: "Question",
                value: faqData.question
              },
              {
                name: "Answer",
                value: faqData.answer
              },
              {
                name: "Deleted By",
                value: user?.username || "Unknown User"
              }
            ],
            timestamp: timestamp
          }
        ]
      };

      // Send webhook notification
      await fetch("/api/webhooks/faq-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });
    } catch (error) {
      console.error("Error sending delete webhook notification:", error);
      // Don't block the main operation if webhook fails
    }
  };

  const handleDeleteFaq = async (id, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      try {
        // Get the FAQ data before deleting
        const faqToDelete = faqs.find(faq => faq.id === id);
        
        // Delete the FAQ
        await pb.collection("faqs").delete(id);
        
        // Send webhook notification
        if (faqToDelete) {
          await sendDeleteWebhookNotification(faqToDelete);
        }
        
        // Refresh the FAQ list
        const records = await pb.collection("faqs").getList(1, 100, {
          sort: "position",
        });
        setFaqs(records.items);
      } catch (err) {
        console.error("Error deleting FAQ:", err);
        setError("Failed to delete FAQ");
      }
    }
  };

  const handleEditorClose = async (refresh) => {
    setEditorOpen(false);
    if (refresh) {
      try {
        const records = await pb.collection("faqs").getList(1, 100, {
          sort: "position",
        });
        setFaqs(records.items);
      } catch (err) {
        console.error("Error refreshing FAQs:", err);
      }
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  if (loading) {
    return (
      <Container>
        <Typography sx={{ mt: 4 }}>Loading FAQs...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Frequently asked questions</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddFaq}
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Add FAQ
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2}>
        {faqs.map((faq) => (
          <Grid
            item
            xs={12}
            md={6}
            key={faq.id}
            sx={{
              width: {
                xs: "100%", // Full width on mobile
                md: "49.3%", // 49.3% width on desktop
              },
            }}
          >
            <Accordion
              expanded={expandedPanel === faq.id}
              onChange={handleAccordionChange(faq.id)}
              sx={{
                backgroundColor: "rgba(40, 44, 52, 0.99)",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${faq.id}-content`}
                id={`panel-${faq.id}-header`}
              >
                <Typography>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    "& code": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      padding: "2px 4px",
                      borderRadius: "3px",
                      fontFamily: "monospace",
                    },
                    "& pre": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      padding: "8px",
                      borderRadius: "4px",
                      overflowX: "auto",
                    },
                    "& strong": {
                      fontWeight: "bold",
                    },
                    "& em": {
                      fontStyle: "italic",
                    },
                    "& u": {
                      textDecoration: "underline",
                    },
                    "& s": {
                      textDecoration: "line-through",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatDiscordMarkdown(faq.answer),
                  }}
                />
                {isAdmin && (
                  <Box
                    sx={{
                      mt: 2,
                      textAlign: "right",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditFaq(faq);
                      }}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteFaq(faq.id, e)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <FAQEditor
        open={editorOpen}
        onClose={handleEditorClose}
        faq={selectedFaq}
      />
    </Container>
  );
};

export default FAQ;
